import React, { useState, useEffect } from 'react';
import { X, Wand2, Loader, Sparkles } from 'lucide-react';
import { adminFetch } from '../../lib/adminAuth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preset room templates admins can start from — clicking one fills the
// textarea so they can tweak rather than type from scratch.
const PRESETS = [
  { label: 'Hampstead townhouse · Heath view', prompt: 'modern Hampstead townhouse living room, mid-century modern furniture, walnut wood floors, tall Georgian sash window on the right side overlooking Hampstead Heath at golden hour, soft warm light, sculptural table lamp, minimal styling' },
  { label: 'Victorian study · rainy London', prompt: "moody Victorian study with deep-green oxblood walls, tall Georgian sash window on the left showing rain-slick London rooftops at dusk, brass reading lamp glowing, antique leather chesterfield chair, worn Persian rug, low warm ambient light" },
  { label: 'Bright drawing room · garden view', prompt: 'bright airy Hampstead drawing room with soft-white walls, tall arched window on the right overlooking a mature English walled garden in full morning sunlight, ivory upholstered sofa, pale limed-oak floors, single vase of white peonies' },
  { label: "Architect's loft · skyline", prompt: "loft-style architect's living space, exposed brick wall on left, floor-to-ceiling steel-framed window on the right with panoramic London skyline at blue hour, city lights just switching on, polished concrete floor, low charcoal linen sofa" },
  { label: 'Provençal dining · olive trees', prompt: 'sun-drenched Provençal-style dining room, whitewashed stone walls, arched window on the right framing silver-green olive trees against Mediterranean cobalt sky, rustic oak dining table, terracotta tile floor' },
  { label: 'Mid-century penthouse · night skyline', prompt: 'mid-century modern penthouse lounge at night, deep-navy accent walls, floor-to-ceiling window on the right with panoramic city skyline glittering at midnight, teak Danish credenza, single sculptural desk lamp, low tan leather chair' },
  { label: 'Chelsea drawing room · Thames', prompt: 'Chelsea drawing room in evening light, tall bay window on the right overlooking the River Thames at golden hour with Albert Bridge lights beginning to glow, plum velvet sofa, brass floor lamp, walnut parquet floor' },
  { label: 'Notting Hill kitchen · walled garden', prompt: 'sun-drenched Notting Hill kitchen with soft-cream cabinetry and marble counters, tall French doors on the right opening onto a walled English garden filled with roses and jasmine, morning light, scrubbed pine breakfast table' },
];

/**
 * CompositeRoomModal — asks the admin for a room description, calls the
 * Nano Banana composite endpoint, and returns the new /api/cloud-media
 * URL via `onGenerated(newUrl)`.
 */
const CompositeRoomModal = ({ open, onClose, artworkSrc, initialPrompt, onGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setPrompt(initialPrompt || PRESETS[0].prompt);
  }, [open, initialPrompt]);

  if (!open) return null;

  const generate = async () => {
    if (!artworkSrc) {
      toast.error('This scene has no source artwork — pick one first.');
      return;
    }
    if (!prompt.trim()) {
      toast.error('Describe the room first');
      return;
    }
    setBusy(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/hero/composite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artwork_url: artworkSrc, room_prompt: prompt.trim() }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (!data.url) throw new Error('No url returned');
      const absUrl = data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
      onGenerated(absUrl, prompt.trim());
      toast.success('Composite generated (unsaved — click Save Changes)');
      onClose();
    } catch (e) {
      toast.error(`Generate failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={busy ? undefined : onClose}
      data-testid="composite-room-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="composite-room-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-black" />
            <div>
              <h3 className="text-lg font-light">Regenerate room composite</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Nano Banana will place the artwork in this room with a window view. ~10s.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-40"
            data-testid="composite-room-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs uppercase tracking-[3px] text-gray-500 mb-2">
              Preset templates
            </label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setPrompt(p.prompt)}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:border-black hover:bg-gray-50 disabled:opacity-40"
                  data-testid={`composite-preset-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[3px] text-gray-500 mb-2">
              Room description
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
              rows={5}
              placeholder="Describe the room, wall colour, furniture, window view, lighting…"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-40 font-sans"
              data-testid="composite-room-prompt"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Tip: mention window placement (left/right), a view (Heath, Thames, garden), and lighting (golden hour, dusk, morning).
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-white disabled:opacity-40"
            data-testid="composite-room-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={busy || !prompt.trim()}
            className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
            data-testid="composite-room-generate"
          >
            {busy ? <Loader className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? 'Generating…' : 'Generate composite'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompositeRoomModal;
